import { createContext, useContext, useEffect } from 'react';
import { TypedDataDomain, TypedDataField } from 'ethers';
import { _TypedDataEncoder, arrayify, hashMessage } from 'ethers/lib/utils';
import { LitNodeClient } from '@lit-protocol/lit-node-client';
import { IRelayPKP } from '@lit-protocol/types';
import { toast } from 'sonner';
import { useLocalStorage } from 'usehooks-ts';
import Intercom, { update } from '@intercom/messenger-js-sdk';
import { decryptEncryptedWallet, signatureToDecryptEncryptedWallet } from '@/lib/auth/decryptEncryptedWallet';
import { delay } from '@/hooks/LitSessionContext/useLitAuth';
import useGetWrappedWallet from '@/hooks/auth/useGetWrappedWallet';
import useSetExpoToken from '@/hooks/nativeApp/useSetExpoToken';
import { useIsMobile } from '@/hooks/utilities/use-mobile';
import useAppDetails from '@/hooks/utilities/useAppDetails';
import useInitRouterPath from '@/hooks/utilities/useInitRouterPath';
import useReferralCode from '@/hooks/onboarding/useReferralCode';
import useLitClients, { ILitClients } from '@/hooks/LitSessionContext/useLitClients';
import useLitSession, { ILitSession } from '@/hooks/LitSessionContext/useLitSession';
import { baseUrl } from '@/lib/misc/const';
import useCheckAppVersion from '@/hooks/nativeApp/useCheckAppVersion';
import ToastLoadingContent from '@/components/shared/ToastLoadingContent';
import useWebNotifications from '@/hooks/notifications/useWebNotifications';
export interface LitSessionContextInterface extends ILitClients, ILitSession {
  ethersPKPWallet: () => {
    address: string;
    signMessage: (message: string) => Promise<string>;
    _signTypedData: (domain: TypedDataDomain, types: Record<string, Array<TypedDataField>>, value: Record<string, any>) => Promise<string>;
  };
}
const LitSessionContext = createContext({} as LitSessionContextInterface);
export const redirectUri = `${baseUrl}/home`;
export const LitSessionProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const {
    litNetwork,
    setLitNetwork,
    clients
  } = useLitClients({
    redirectUri
  });
  const [startDecryption, setStartDecryption] = useLocalStorage('startDecryption', false);
  const {
    userInfo,
    litSession,
    PKP,
    setUsingTurnKey,
    setLitSession,
    clearLitSession,
    PKPNetwork,
    connectedWalletAddress,
    setPKPNetwork,
    setAllPKPs,
    allPKPs,
    setPKP,
    currentAuthMethod,
    setCurrentAuthMethod,
    MFAInfo,
    setMFAInfo,
    sessionLength,
    setSessionLength,
    setEmulationPKP,
    userPKP,
    setDecryptedWallets,
    decryptedWallets
  } = useLitSession();
  const {
    litCachedMinSig,
    setLitCachedMinSig,
    setCipher1,
    setCipher2,
    setCipher3
  } = useGetWrappedWallet();
  const {
    isAppleNativeApp
  } = useAppDetails();
  const [_, setHasInitatedApple] = useLocalStorage('hasInitatedApple', false);
  useSetExpoToken(PKP);
  useWebNotifications(PKP);
  useCheckAppVersion();
  useInitRouterPath();

  //handles prefilling referral code
  useReferralCode();
  function GeniusWallet() {
    return {
      address: PKP?.ethAddress,
      signMessage: async (message: string): Promise<string> => {
        const client = clients?.litNodeClient;
        const prefixedMessage = hashMessage(message);
        const bytesMessage = arrayify(prefixedMessage);
        const pkpSignRes = await client?.pkpSign({
          toSign: bytesMessage,
          pubKey: PKP.publicKey,
          sessionSigs: litSession
        });
        return pkpSignRes?.signature;
      },
      _signTypedData: async (domain: TypedDataDomain, types: Record<string, Array<TypedDataField>>, value: Record<string, any>): Promise<string> => {
        try {
          const populated = await _TypedDataEncoder.resolveNames(domain, types, value, (name: string) => {
            if (this.provider == null) {
              throw new Error('cannot resolve ENS names without a provider');
            }
            return this.provider.resolveName(name);
          });
          const toSign = _TypedDataEncoder.hash(populated.domain, types, populated.value);
          const toSignBytes = arrayify(toSign);
          if (toSignBytes.length !== 32) {
            throw new Error('hash of typed data is not 32 bytes');
          }
          const client = clients?.litNodeClient;
          const pkpSignRes = await client?.pkpSign({
            toSign: toSignBytes,
            pubKey: PKP.publicKey,
            sessionSigs: litSession
          });
          return pkpSignRes?.signature;
        } catch (e) {
          console.error('ERROR in PKP Signing', e);
          throw e;
        }
      }
    };
  }
  async function setUpDecryptedWallets() {
    globalThis.initiatedDecryption = true;
    setHasInitatedApple(true);
    let id;
    console.log('WUJI start set up decrypted wallets', MFAInfo, !MFAInfo?.presentMFAInfo);
    if (!MFAInfo?.presentMFAInfo?.authId) {
      id = toast(<ToastLoadingContent content='Finishing setting up your account' />);
    }
    try {
      // Saves signature in local storage
      let nodeClient = clients?.litNodeClient;
      if (!nodeClient?.ready) {
        nodeClient = new LitNodeClient({
          litNetwork,
          debug: true,
          rpcUrl: process.env.NEXT_PUBLIC_LIT_RPC_URL
        });
        await nodeClient.connect();
      }
      const cacheCallSig = await signatureToDecryptEncryptedWallet({
        PKP: PKP as IRelayPKP,
        litSession,
        litNodeClient: nodeClient,
        litCachedMinSig,
        setLitCachedMinSig
      });
      // const suiPrivateKeyPromise = decryptEncryptedWallet({
      //   PKP: PKP as IRelayPKP,
      //   litSession,
      //   litNodeClient: nodeClient,
      //   encryptedWallet: decryptedWallets?.find((w) => w?.type === 'Sui'),
      //   signature: cacheCallSig,
      //   setCipher1,
      //   setCipher2,
      //   setCipher3,
      // });
      const evmPrivateKeyPromise = decryptEncryptedWallet({
        PKP: PKP as IRelayPKP,
        litSession,
        litNodeClient: nodeClient,
        encryptedWallet: decryptedWallets?.find(w => w?.type === 'EVM'),
        signature: cacheCallSig,
        setCipher1,
        setCipher2,
        setCipher3
      });
      const solanaPrivateKeyPromise = decryptEncryptedWallet({
        PKP: PKP as IRelayPKP,
        litSession: litSession,
        litNodeClient: nodeClient,
        encryptedWallet: decryptedWallets?.find(w => w?.type === 'Solana'),
        signature: cacheCallSig,
        setCipher1,
        setCipher2,
        setCipher3
      });
      const suiPrivateKey = null;
      const [evmPrivateKey, solanaPrivateKey] = await Promise.all([evmPrivateKeyPromise, solanaPrivateKeyPromise
      // suiPrivateKeyPromise,
      ]);
      // console.log('WUJI decrypted wallets', evmPrivateKey, solanaPrivateKey, suiPrivateKey);

      if (evmPrivateKey.error || solanaPrivateKey.error || suiPrivateKey?.error || !evmPrivateKey?.privateKey || !solanaPrivateKey?.keypair) {
        throw new Error('Error in decrypting wallets');
      } else {
        if (!MFAInfo?.presentMFAInfo?.authId) {
          toast.success('Ready to go', {
            id
          });
          if (isAppleNativeApp) {
            //shallow reload to get new session
            //hack we have to do on apple to not log people out
            setStartDecryption(false);
            // await delay(500);
            // window.location.reload();
          }
          await delay(1000);
          toast.dismiss(id);
        }
      }
    } catch (e) {
      console.error('ERROR in setUpDecryptedWallets', e.message);
      if (MFAInfo?.presentMFAInfo?.authId) {
        toast.error('Error in setting up your account. Please log in and back out again', {
          duration: 5000
        });
      } else {
        toast.error('Error in setting up your account. Please log in and back out again', {
          id,
          duration: 5000
        });
      }
    } finally {
      setStartDecryption(false);
    }
  }
  useEffect(() => {
    if (startDecryption && PKP?.publicKey && PKP?.ethAddress && decryptedWallets?.find(w => w?.type === 'EVM') && decryptedWallets?.find(w => w?.type === 'Solana') && decryptedWallets?.find(w => w?.type === 'Sui') && !globalThis.initiatedDecryption) {
      setUpDecryptedWallets();
    }
  }, [startDecryption, decryptedWallets, PKP?.publicKey, PKP?.ethAddress]);
  const isMobile = useIsMobile();
  useEffect(() => {
    if (!PKP?.ethAddress || isMobile) return;
    Intercom({
      app_id: 'ei6xmnio',
      user_id: PKP?.username,
      // IMPORTANT: Replace "user.id" with the variable you use to capture the user's ID
      name: PKP?.firstName,
      // IMPORTANT: Replace "user.name" with the variable you use to capture the user's name
      email: PKP?.email // IMPORTANT: Replace "user.email" with the variable you use to capture the user's email
    });
    update({
      hide_default_launcher: false
    });
  }, [PKP]);
  return <LitSessionContext.Provider value={{
    //if wallet is connected directly to front end
    connectedWalletAddress,
    //user info from postgres db
    userInfo,
    //lit session sigs
    litSession,
    //main pkp wallet
    PKP,
    //setting active pkp
    setPKP,
    setUsingTurnKey,
    //manually overriding session if needed
    setLitSession,
    //clearing session
    clearLitSession,
    //ethers based pkp wallet for signing messages with cloudwallet
    ethersPKPWallet: GeniusWallet,
    //evm network for pkp wallet
    PKPNetwork,
    //setting evm network for pkp wallet
    setPKPNetwork,
    //lit network for lit node client
    litNetwork,
    //setting lit network for lit node client
    setLitNetwork,
    //lit node client and auth clients
    clients,
    //setting all pkps
    allPKPs,
    setAllPKPs,
    //current auth method
    currentAuthMethod,
    //setting current auth method
    setCurrentAuthMethod,
    MFAInfo,
    setMFAInfo,
    //session length
    sessionLength,
    //setting session length
    setSessionLength,
    //if dev account is signed in, set Emulation information for debugging frontend
    setEmulationPKP,
    userPKP,
    setDecryptedWallets,
    decryptedWallets
  }} data-sentry-element="unknown" data-sentry-component="LitSessionProvider" data-sentry-source-file="LitSessionProvider.tsx">
      {children}
    </LitSessionContext.Provider>;
};
export const useSession = () => {
  const sessionValues = useContext(LitSessionContext);
  return sessionValues;
};